import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft,
  BadgeCheck,
  BadgeX,
  CheckCircle2,
  Edit3,
  FileText,
  Store,
  XCircle,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { formatDate, formatDateTime, formatMoney } from "@/lib/utils";
import { useListQuery } from "@/hooks/useListQuery";
import { DataTable, type Column } from "@/components/data/DataTable";
import {
  ActiveFilters,
  FilterSelect,
  PageHeader,
  SearchInput,
  Toolbar,
} from "@/components/data/toolbar";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from "@/components/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/overlays";
import { ConfirmDialog } from "@/components/data/ConfirmDialog";

interface BreederRow {
  id: string;
  name: string;
  bio?: string | null;
  verified: boolean;
  is_approved: boolean;
  is_suspended: boolean;
  status: string;
  city: string | null;
  has_pet_shop: boolean;
  avg_rating: number;
  review_count: number;
  member_since: string | null;
  created_at: string;
  user: { id: string; phone: string | null; email: string | null; status: string };
  _count: { listings: number; reviews: number; contacts_unlocked: number };
}

const FILTERS = ["status", "verified", "city"] as const;

export const BreedersListPage = () => {
  const navigate = useNavigate();

  const list = useListQuery<BreederRow>({
    key: "breeders",
    path: "/breeders",
    filterKeys: FILTERS,
    defaultSort: "created_at",
  });

  const columns: Column<BreederRow>[] = [
    {
      key: "name",
      header: "Breeder / Shop",
      sortable: true,
      render: (b) => (
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 truncate font-semibold text-foreground">
            {b.name}
            {b.has_pet_shop && (
              <Badge variant="secondary" className="text-[10px] py-0 px-1">Shop</Badge>
            )}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {b.user?.phone ? `+91 ${b.user.phone}` : b.user?.email || "—"}
          </p>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (b) => (
        <div className="flex flex-wrap items-center gap-1">
          {b.verified ? (
            <Badge variant="success" className="gap-1">
              <BadgeCheck className="h-3 w-3" /> Verified
            </Badge>
          ) : b.is_approved ? (
            <Badge variant="default">Approved</Badge>
          ) : b.status === "rejected" ? (
            <Badge variant="destructive">Rejected</Badge>
          ) : (
            <Badge variant="warning">Pending Review</Badge>
          )}
        </div>
      ),
    },
    {
      key: "city",
      header: "City",
      render: (b) => (
        <span className="text-sm text-foreground">{b.city ?? "—"}</span>
      ),
    },
    {
      key: "listings",
      header: "Listings",
      align: "center",
      render: (b) => (
        <Badge variant="secondary">{b._count?.listings || 0} active</Badge>
      ),
    },
    {
      key: "rating",
      header: "Rating",
      align: "right",
      render: (b) => (
        <span className="text-xs font-semibold text-foreground">
          {b.avg_rating > 0 ? `${b.avg_rating.toFixed(1)} ★ (${b._count?.reviews || 0})` : "No reviews"}
        </span>
      ),
    },
    {
      key: "created_at",
      header: "Joined",
      sortable: true,
      align: "right",
      render: (b) => (
        <span className="text-xs text-muted-foreground">
          {formatDate(b.created_at)}
        </span>
      ),
    },
  ];

  const activeFilters = React.useMemo(() => {
    const out: { key: string; label: string }[] = [];
    if (list.q) out.push({ key: "q", label: `Search: "${list.q}"` });
    if (list.filters.status) out.push({ key: "status", label: `Status: ${list.filters.status}` });
    if (list.filters.verified) out.push({ key: "verified", label: list.filters.verified === "true" ? "Verified" : "Unverified" });
    return out;
  }, [list.q, list.filters]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Breeder Directory"
        description="Review, verify, and moderate registered pet breeders and shops."
      />

      <Toolbar>
        <SearchInput
          value={list.q}
          onChange={list.setQ}
          placeholder="Search by breeder name, city, phone..."
        />
        <FilterSelect
          label="Status"
          value={list.filters.status}
          onChange={(v) => list.setFilter("status", v)}
          options={[
            { label: "Pending", value: "pending" },
            { label: "Approved", value: "approved" },
            { label: "Rejected", value: "rejected" },
          ]}
        />
        <FilterSelect
          label="Verified"
          value={list.filters.verified}
          onChange={(v) => list.setFilter("verified", v)}
          options={[
            { label: "Verified Only", value: "true" },
            { label: "Unverified Only", value: "false" },
          ]}
        />
      </Toolbar>

      <ActiveFilters
        filters={activeFilters}
        onClear={(key) => (key === "q" ? list.setQ("") : list.setFilter(key, undefined))}
        onClearAll={list.clearFilters}
      />

      <DataTable
        caption="All registered breeders"
        columns={columns}
        rows={list.rows}
        rowKey={(b) => b.id}
        loading={list.loading}
        total={list.total}
        onRowClick={(row) => navigate(`/breeders/${row.id}`)}
        nextCursor={list.nextCursor}
        onLoadMore={list.loadMore}
        loadingMore={list.loadingMore}
      />
    </div>
  );
};

// ── Detail Page ────────────────────────────────────────────────────────

export const BreederDetailPage = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [action, setAction] = React.useState<
    null | "approve" | "reject" | "verify" | "unverify" | "suspend" | "unsuspend"
  >(null);

  // Edit Breeder Profile state
  const [editOpen, setEditOpen] = React.useState(false);
  const [editName, setEditName] = React.useState("");
  const [editCity, setEditCity] = React.useState("");
  const [editBio, setEditBio] = React.useState("");
  const [editHasPetShop, setEditHasPetShop] = React.useState(false);

  const detail = useQuery({
    queryKey: ["breeders", id],
    queryFn: () => api.get<any>(`/breeders/${id}`),
    enabled: Boolean(id),
  });

  React.useEffect(() => {
    if (detail.data) {
      setEditName(detail.data.name || "");
      setEditCity(detail.data.city || "");
      setEditBio(detail.data.bio || "");
      setEditHasPetShop(Boolean(detail.data.has_pet_shop));
    }
  }, [detail.data]);

  const mutate = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) =>
      api.postWithMessage(`/breeders/${id}${path}`, body),
    onSuccess: ({ message }) => {
      toast.success(message);
      setAction(null);
      void queryClient.invalidateQueries({ queryKey: ["breeders", id] });
      void queryClient.invalidateQueries({ queryKey: ["breeders"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "kpis"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Action failed"),
  });

  const updateMutate = useMutation({
    mutationFn: (body: unknown) =>
      api.putWithMessage(`/breeders/${id}`, body),
    onSuccess: ({ message }) => {
      toast.success(message);
      setEditOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["breeders", id] });
      void queryClient.invalidateQueries({ queryKey: ["breeders"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Update failed"),
  });

  if (detail.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-36 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (detail.error || !detail.data) {
    return (
      <EmptyState
        icon={Store}
        title="Could not load this breeder"
        description={detail.error instanceof Error ? detail.error.message : undefined}
        action={
          <Button variant="outline" onClick={() => navigate("/breeders")}>
            Back to breeders
          </Button>
        }
      />
    );
  }

  const b = detail.data;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon-sm" asChild>
          <Link to="/breeders" aria-label="Back to breeders">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <p className="text-sm font-medium text-muted-foreground">Breeder Directory / Profile</p>
      </div>

      {/* Header Profile Card */}
      <Card>
        <CardContent className="flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex items-start gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-indigo-500/10 text-xl text-indigo-500">
              <Store className="h-7 w-7" />
            </span>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold tracking-tight text-foreground">{b.name}</h1>
                {b.verified ? (
                  <Badge variant="success" className="gap-1">
                    <BadgeCheck className="h-3 w-3" /> Verified Badge
                  </Badge>
                ) : (
                  <Badge variant="warning">Unverified</Badge>
                )}
                <Badge variant={b.status === "approved" ? "default" : b.status === "rejected" ? "destructive" : "warning"}>
                  {b.status}
                </Badge>
                {b.has_pet_shop && <Badge variant="secondary">Registered Pet Shop</Badge>}
              </div>

              <p className="mt-1 text-sm text-muted-foreground">
                {[b.city, b.member_since && `Member ${b.member_since}`].filter(Boolean).join(" · ")}
              </p>

              <p className="mt-1 text-xs text-muted-foreground">
                Account Owner:{" "}
                <Link to={`/users/${b.user?.id}`} className="font-semibold text-primary hover:underline">
                  {b.user?.name || "Admin User"} ({b.user?.phone ? `+91 ${b.user.phone}` : b.user?.email || "—"})
                </Link>
              </p>

              {b.bio && (
                <div className="mt-3 rounded-lg border border-border bg-surface/50 p-3">
                  <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-1">About / Bio</p>
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap">{b.bio}</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditName(b.name || "");
                setEditCity(b.city || "");
                setEditBio(b.bio || "");
                setEditHasPetShop(Boolean(b.has_pet_shop));
                setEditOpen(true);
              }}
              className="gap-1.5"
            >
              <Edit3 className="h-4 w-4" /> Edit Profile &amp; Bio
            </Button>

            {b.status !== "approved" && (
              <Button
                variant="success"
                size="sm"
                onClick={() => setAction("approve")}
                className="gap-1.5"
              >
                <CheckCircle2 className="h-4 w-4" /> Approve Breeder
              </Button>
            )}

            {b.status !== "rejected" && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setAction("reject")}
                className="gap-1.5"
              >
                <XCircle className="h-4 w-4" /> Reject / Suspend
              </Button>
            )}

            {b.verified ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAction("unverify")}
                className="gap-1.5"
              >
                <BadgeX className="h-4 w-4 text-warning" /> Revoke Badge
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAction("verify")}
                className="gap-1.5"
              >
                <BadgeCheck className="h-4 w-4 text-success" /> Grant Verified Badge
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{b._count?.listings || 0}</p>
          <p className="text-xs text-muted-foreground">Active Listings</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{b.avg_rating > 0 ? `${b.avg_rating.toFixed(1)} ★` : "—"}</p>
          <p className="text-xs text-muted-foreground">Avg Customer Rating</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{b._count?.reviews || 0}</p>
          <p className="text-xs text-muted-foreground">Total Reviews</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{b.documents?.length || 0}</p>
          <p className="text-xs text-muted-foreground">Uploaded Documents</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="documents">
        <TabsList>
          <TabsTrigger value="documents">Uploaded Documents ({b.documents?.length || 0})</TabsTrigger>
          <TabsTrigger value="listings">Pet Listings ({b.listings?.length || 0})</TabsTrigger>
          <TabsTrigger value="reviews">Reviews ({b.reviews?.length || 0})</TabsTrigger>
          <TabsTrigger value="verifications">Verification History</TabsTrigger>
        </TabsList>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Identity &amp; Shop Documents</CardTitle>
              <CardDescription>
                Review submitted documents for Aadhaar, PAN, Pet Shop License, etc.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(!b.documents || b.documents.length === 0) ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No documents uploaded for this breeder profile yet.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                  {b.documents.map((doc: any) => (
                    <div key={doc.id} className="overflow-hidden rounded-xl border border-border bg-surface/40 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {doc.doc_type?.replace(/_/g, " ")}
                        </span>
                        <Badge variant={doc.status === "approved" ? "success" : "warning"}>
                          {doc.status}
                        </Badge>
                      </div>
                      {doc.doc_url ? (
                        <a href={doc.doc_url} target="_blank" rel="noopener noreferrer" className="group block">
                          <img
                            src={doc.doc_url}
                            alt={doc.doc_type}
                            className="h-40 w-full rounded-lg object-cover transition-transform group-hover:scale-[1.02]"
                          />
                        </a>
                      ) : (
                        <div className="flex h-40 w-full items-center justify-center rounded-lg bg-surface text-muted-foreground">
                          <FileText className="h-8 w-8" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Listings Tab */}
        <TabsContent value="listings">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Breeder Pet Listings</CardTitle>
            </CardHeader>
            <CardContent>
              {(!b.listings || b.listings.length === 0) ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No listings published by this breeder.
                </p>
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {b.listings.map((l: any) => (
                    <div key={l.id} className="overflow-hidden rounded-xl border border-border bg-card p-3">
                      {l.photos?.[0] && (
                        <img src={l.photos[0]} alt={l.name} className="h-36 w-full rounded-lg object-cover mb-2" />
                      )}
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-foreground">{l.name}</p>
                        <Badge variant={l.status === "available" ? "success" : "secondary"}>{l.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{l.breed} · <span className="capitalize">{l.type}</span></p>
                      <p className="mt-1 font-mono font-bold text-foreground">{formatMoney(Number(l.price))}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Customer Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              {(!b.reviews || b.reviews.length === 0) ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No reviews posted for this breeder yet.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {b.reviews.map((r: any) => (
                    <li key={r.id} className="py-3">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-foreground">{r.buyer?.name || "Buyer"}</p>
                        <span className="text-xs font-bold text-warning">{Number(r.rating).toFixed(1)} ★</span>
                      </div>
                      {r.comment && <p className="mt-1 text-sm text-muted-foreground">{r.comment}</p>}
                      <p className="mt-1 text-[11px] text-muted-foreground/60">{formatDate(r.created_at)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Verification History Tab */}
        <TabsContent value="verifications">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Verification History</CardTitle>
            </CardHeader>
            <CardContent>
              {(!b.verification_requests || b.verification_requests.length === 0) ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No verification requests on record.
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {b.verification_requests.map((v: any) => (
                    <li key={v.id} className="py-3">
                      <div className="flex items-center justify-between">
                        <Badge variant={v.status === "approved" ? "success" : v.status === "rejected" ? "destructive" : "warning"}>
                          {v.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{formatDateTime(v.submitted_at)}</span>
                      </div>
                      {v.admin_notes && <p className="mt-1 text-xs text-muted-foreground">Notes: {v.admin_notes}</p>}
                      {v.rejection_reason && <p className="mt-1 text-xs text-destructive">Reason: {v.rejection_reason}</p>}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialogs */}
      <ConfirmDialog
        open={action === "approve"}
        onOpenChange={(open) => !open && setAction(null)}
        title="Approve Breeder Profile?"
        description="This will mark the breeder as approved and grant the verified status."
        confirmLabel="Approve Breeder"
        variant="default"
        loading={mutate.isPending}
        onConfirm={() => mutate.mutate({ path: "/approve" })}
      />

      <ConfirmDialog
        open={action === "reject"}
        onOpenChange={(open) => !open && setAction(null)}
        title="Reject / Suspend Breeder?"
        description="This will reject this breeder and remove their approved/verified status."
        confirmLabel="Reject Breeder"
        variant="destructive"
        requireReason
        reasonLabel="Rejection Reason"
        reasonHint="Provide a clear reason for rejecting this breeder."
        loading={mutate.isPending}
        onConfirm={(reason) => mutate.mutate({ path: "/reject", body: { reason } })}
      />

      <ConfirmDialog
        open={action === "verify"}
        onOpenChange={(open) => !open && setAction(null)}
        title="Grant Verified Badge?"
        description="This will add the verified checkmark badge to this breeder profile."
        confirmLabel="Grant Badge"
        variant="default"
        loading={mutate.isPending}
        onConfirm={() => mutate.mutate({ path: "/verify" })}
      />

      <ConfirmDialog
        open={action === "unverify"}
        onOpenChange={(open) => !open && setAction(null)}
        title="Revoke Verified Badge?"
        description="This will remove the verified badge from this breeder."
        confirmLabel="Revoke Badge"
        variant="destructive"
        loading={mutate.isPending}
        onConfirm={() => mutate.mutate({ path: "/unverify" })}
      />

      {/* Edit Breeder Profile & Bio Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Edit Breeder Profile &amp; Bio</DialogTitle>
            <DialogDescription>
              Update business details, public bio/about summary, and pet shop certification visible to buyers.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              updateMutate.mutate({
                name: editName.trim(),
                city: editCity.trim() || null,
                bio: editBio.trim() || null,
                has_pet_shop: editHasPetShop,
              });
            }}
            className="space-y-4 py-2"
          >
            <Field label="Breeder / Kennel / Shop Name" htmlFor="edit-name" required>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="e.g. Royal Paws Kennel"
                required
              />
            </Field>

            <Field label="City / Location" htmlFor="edit-city">
              <Input
                id="edit-city"
                value={editCity}
                onChange={(e) => setEditCity(e.target.value)}
                placeholder="e.g. Pune"
              />
            </Field>

            <Field label="About / Bio (Shown to Buyers)" htmlFor="edit-bio" hint="Tell buyers about specialty breeds, kennel experience, health care, and authentic pedigree.">
              <Textarea
                id="edit-bio"
                value={editBio}
                onChange={(e) => setEditBio(e.target.value)}
                placeholder="Write an about summary for this breeder..."
                className="min-h-[120px]"
                maxLength={1000}
              />
            </Field>

            <div className="flex items-center justify-between rounded-lg border border-border bg-surface p-3">
              <div>
                <p className="text-sm font-medium text-foreground">Pet Shop Registration</p>
                <p className="text-xs text-muted-foreground">Certified facility badge displayed on public profile.</p>
              </div>
              <Switch
                checked={editHasPetShop}
                onCheckedChange={setEditHasPetShop}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditOpen(false)}
                disabled={updateMutate.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={updateMutate.isPending}
                disabled={!editName.trim()}
              >
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
